import { castArray, omit } from 'lodash-es';
import CancellationError from '@/common/utils/errors/CancellationError';

type ThrottledActionFn = (abortController: AbortController) => Promise<void>;

type ThrottledCall = {
  run: () => void;
  cancel: () => void;
  state: 'idle' | 'pending';
}

type ActionFunction<T> = (id: ID | ID[], options?: { abortController?: AbortController }) => Promise<T>;

type CallbackFunction<T> = (result: T, idsProcessed: ID[]) => void;

export type AccumulatedAction = {
  (id: ID | ID[]): void;
  clearId: (id: ID | ID[]) => void;
  clear: () => void;
}

const createThrottledCall = (action: ThrottledActionFn, threshold: number): ThrottledCall => {
  let state: 'idle' | 'pending' = 'idle';
  let chainCall: (() => void) | undefined;
  let timeout: ReturnType<typeof setTimeout> | undefined;
  let timestamp: number | undefined;
  let abortController: AbortController | null = null;

  const clear = (): void => {
    state = 'idle';
    clearTimeout(timeout);
    timestamp = undefined;
    chainCall = undefined;
  };

  const cancel = (): void => {
    if (abortController) {
      abortController.abort();
    } else {
      clear();
    }
  };

  const run = (): void => {
    if (threshold && timestamp && Date.now() - timestamp < threshold) {
      if (state === 'pending') {
        chainCall = () => run();
      }

      return;
    }

    cancel();
    const timeoutDelay = timestamp ? threshold - (Date.now() - timestamp) : 0;
    timestamp = Date.now();
    timeout = setTimeout(async () => {
      try {
        state = 'pending';
        abortController = new AbortController();
        await action(abortController);

        if (chainCall) {
          const chainCallDup = chainCall;
          clear();
          chainCallDup();
        }
      } finally {
        clear();
        abortController = null;
      }
    }, timeoutDelay);
  };

  return {
    run,
    cancel,
    state
  };
};

export default function useAccumulatedAction<T>(
  action: ActionFunction<T>,
  callback: CallbackFunction<T> = () => {},
  threshold = 2000
): AccumulatedAction {
  const ids: ID[] = [];

  const actionFn = async (abortController: AbortController): Promise<void> => {
    let res: T;

    const idsToProcess = ids.slice();
    ids.length = 0;

    if (idsToProcess.length === 0) {
      return;
    }

    try {
      res = await action(idsToProcess, { abortController });
      callback(res, idsToProcess);
    } catch (error) {
      if (error instanceof CancellationError) {
        idsToProcess.forEach(id => !ids.includes(id) && ids.push(id));
        return;
      }

      throw error;
    }
  };

  const throttled = createThrottledCall(actionFn, threshold);
  const runner = {
    run: (id: ID | ID[]) => {
      castArray(id)
        .forEach(idItem => !ids.includes(idItem) && ids.push(idItem));

      return throttled.run();
    },
    clearId: (id: ID | ID[]) => {
      if (throttled.state === 'pending') {
        throttled.cancel();
      }
      castArray(id)
        .forEach(idItem => {
          if (ids.includes(idItem)) {
            ids.splice(ids.indexOf(idItem), 1);
          }
        });

      if (ids.length) {
        throttled.run();
      } else {
        throttled.cancel();
      }
    },
    clear: () => {
      ids.length = 0;
      throttled.cancel();
    }
  };

  const accumulatedAction = Object.assign(runner.run, omit(runner, 'run'));
  return accumulatedAction;
}
